import { createContext, useContext, type ReactNode } from 'react';
import { useSipPhone } from '@/hooks/useSipPhone';
import { useUserRoleContext } from './UserRoleContext';

type SipPhoneValue = ReturnType<typeof useSipPhone>;

const SipPhoneContext = createContext<SipPhoneValue | undefined>(undefined);

// Mounted once at the app root (see App.tsx), same "persists across
// navigation" reasoning as UserRoleProvider — a per-page useSipPhone() call
// would re-register the SIP socket (and drop any in-progress incoming-call
// state) every time a staff member clicked to a different admin page,
// since each admin route remounts its whole page tree. `enabled` is gated
// on having any staff role at all, so the public storefront (this same app,
// shared with anonymous customers) never tries to register a softphone.
export function SipPhoneProvider({ children }: { children: ReactNode }) {
  const { role } = useUserRoleContext();
  const phone = useSipPhone(!!role);
  return <SipPhoneContext.Provider value={phone}>{children}</SipPhoneContext.Provider>;
}

export function useSipPhoneContext(): SipPhoneValue {
  const ctx = useContext(SipPhoneContext);
  if (!ctx) throw new Error('useSipPhoneContext must be used within <SipPhoneProvider>');
  return ctx;
}
